import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Menu, ArrowRight, Leaf, Mail, MapPin } from "lucide-react";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Missione", href: "/mission" },
  { label: "Risultati", href: "/results" },
  { label: "Risorse", href: "/resources" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-[#085041] flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif", color: '#085041' }}>
              givenda
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                  location.pathname === link.href
                    ? "bg-[#085041]/10 text-[#2C2C2A]"
                    : "text-[#888780] hover:text-[#2C2C2A] hover:bg-[#085041]/5"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link to="/book-a-call">
              <Button
                className="rounded-full px-6 h-10 bg-[#1D9E75] hover:bg-[#17845F] text-white font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                data-testid="header-cta-button"
              >
                Prenota una Chiamata
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          {/* Mobile Nav */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-[#F5F3ED]">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex flex-col gap-6 mt-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    className={`text-lg font-medium transition-colors ${
                      location.pathname === link.href ? "text-[#2C2C2A]" : "text-[#888780] hover:text-[#2C2C2A]"
                    }`}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link to="/book-a-call" onClick={() => setOpen(false)}>
                  <Button className="w-full rounded-full bg-[#1D9E75] hover:bg-[#17845F] text-white" data-testid="mobile-cta-button">
                    Prenota una Chiamata
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#085041] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
                <Leaf className="w-5 h-5 text-[#D85A30]" />
              </div>
              <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>
                givenda
              </span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">
              Aiutiamo le organizzazioni no-profit ad amplificare la loro missione attraverso la crescita digitale, fondi per il marketing e per le AI e siti web ad alta conversione.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/40 mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              Navigazione
            </h4>
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-white/70 hover:text-white text-sm transition-colors"
                  data-testid={`footer-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/book-a-call" className="text-white/70 hover:text-white text-sm transition-colors" data-testid="footer-link-book-call">
                Prenota una Chiamata
              </Link>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/40 mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              Servizi
            </h4>
            <div className="flex flex-col gap-3 text-sm text-white/70">
              <span>Fondi per il Marketing</span>
              <span>Creazione Siti Web</span>
              <span>SEO e Contenuti</span>
              <span>Costruzione Comunità</span>
              <span>Soluzioni CRM Tutto in Uno</span>
              <span>Consulenza</span>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/40 mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              Contatti
            </h4>
            <div className="flex flex-col gap-3 text-sm text-white/70">
              <a href="mailto:hello@givenda.com" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="footer-email">
                <Mail className="w-4 h-4" /> hello@givenda.com
              </a>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Full remote, Italia
              </span>
            </div>
          </div>
        </div>

        <Separator className="my-10 bg-white/10" />

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <p>&copy; {new Date().getFullYear()} Givenda. Tutti i diritti riservati.</p>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white/70 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-white/70 transition-colors">Termini di Servizio</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-16 md:pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
}
